import { Check, Copy } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import {
  formatStatusText,
  getStatusDotColor,
  getStatusTextColor,
} from "@/utils/statusColors";
import { PanelLink } from "@/v2/components/panel/PanelLink";
import type { PanelEntityType } from "@/v2/stores/panelStore";
import { DisplayValue } from "@/v2/ui/primitives/display-value";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";
import { TruncatedCell } from "@/v2/ui/primitives/truncated-cell";

interface StatusCellProps {
  status: string | undefined | null;
  formatStatus?: (status: string | undefined) => string;
  getDotColor?: (status: string | undefined | null) => string;
  getTextColor?: (status: string | undefined | null) => string;
}

export const StatusCell = React.memo(function StatusCell({
  status,
  formatStatus = formatStatusText,
  getDotColor = getStatusDotColor,
  getTextColor = getStatusTextColor,
}: StatusCellProps) {
  return (
    <span className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
      <span
        className={cn(
          "tw-w-2 tw-h-2 tw-rounded-full tw-flex-shrink-0",
          getDotColor(status)
        )}
      />
      <span className={getTextColor(status)}>
        {formatStatus(status ?? undefined)}
      </span>
    </span>
  );
});

interface LinkCellProps {
  href: string | undefined;
  children: React.ReactNode;
  target?: "_blank" | "_self";
  truncate?: number;
  className?: string;
  fallback?: React.ReactNode;
  showTooltip?: boolean;
}

export const LinkCell = React.memo(function LinkCell({
  href,
  children,
  target = "_blank",
  truncate,
  className,
  fallback = <DisplayValue value={null} />,
  showTooltip = false,
}: LinkCellProps) {
  if (!href) return <>{fallback}</>;

  const content = truncate ? (
    <TruncatedCell maxWidth={truncate}>
      <span
        className={cn(
          "tw-text-sm tw-text-primary hover:tw-underline",
          className
        )}
      >
        {children}
      </span>
    </TruncatedCell>
  ) : (
    <span
      className={cn("tw-text-sm tw-text-primary hover:tw-underline", className)}
    >
      {children}
    </span>
  );

  const link = (
    <Link
      to={href}
      target={target}
      className="tw-text-sm tw-text-primary hover:tw-underline"
    >
      {content}
    </Link>
  );

  if (showTooltip && children) {
    return (
      <Tooltip>
        <TooltipTrigger asChild={true}>{link}</TooltipTrigger>
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
});

interface DateCellProps {
  date: string | Date | undefined | null;
  formatDate: (date: string | undefined | null) => string;
  className?: string;
}

export const DateCell = React.memo(function DateCell({
  date,
  formatDate,
  className,
}: DateCellProps) {
  const dateStr = date instanceof Date ? date.toISOString() : date;
  return (
    <span
      className={cn(
        "tw-text-sm tw-text-muted-foreground tw-whitespace-nowrap",
        className
      )}
    >
      <DisplayValue value={formatDate(dateStr)} />
    </span>
  );
});

interface TextCellProps {
  value: string | number | undefined | null;
  truncate?: number;
  className?: string;
  fontWeight?: "normal" | "medium" | "semibold" | "bold";
}

export const TextCell = React.memo(function TextCell({
  value,
  truncate,
  className,
  fontWeight = "normal",
}: TextCellProps) {
  const fontClass = {
    normal: "",
    medium: "tw-font-medium",
    semibold: "tw-font-semibold",
    bold: "tw-font-bold",
  }[fontWeight];

  if (truncate) {
    return (
      <TruncatedCell maxWidth={truncate}>
        <DisplayValue
          value={value}
          className={cn("tw-text-sm", fontClass, className)}
        />
      </TruncatedCell>
    );
  }

  return (
    <DisplayValue
      value={value}
      className={cn("tw-text-sm", fontClass, className)}
    />
  );
});

const BASE_PATH_TO_ENTITY_TYPE: Record<string, PanelEntityType> = {
  "/v2/dashboard/stations": "station",
  "/v2/dashboard/docks": "dock",
  "/v2/dashboard/bike": "vehicle",
  "/v2/dashboard/bikes": "vehicle",
  "/v2/dashboard/customer": "user",
  "/v2/dashboard/ledger": "user",
  "/v2/dashboard/transaction": "transaction",
  "/v2/dashboard/rentals": "rental",
  "/v2/dashboard/locks": "lock",
  "/v2/dashboard/cities": "city",
};

function deriveEntityType(basePath: string): PanelEntityType | null {
  return BASE_PATH_TO_ENTITY_TYPE[basePath] ?? null;
}

interface EntityLinkCellProps {
  code: string | number | undefined | null;
  name?: string | undefined | null;
  basePath: string;
  searchParam?: string;
  truncate?: number;
  target?: "_blank" | "_self";
  entityType?: PanelEntityType;
}

export const EntityLinkCell = React.memo(function EntityLinkCell({
  code,
  name,
  basePath,
  searchParam = "search",
  truncate = 150,
  target = "_blank",
  entityType: entityTypeProp,
}: EntityLinkCellProps) {
  if (!code) return <DisplayValue value={null} />;

  const href = `${basePath}?${searchParam}=${encodeURIComponent(String(code))}`;
  const displayText = name || String(code);
  const entityType = entityTypeProp ?? deriveEntityType(basePath);

  if (entityType) {
    return (
      <PanelLink
        entityType={entityType}
        entityId={String(code)}
        fallbackHref={href}
      >
        <TruncatedCell maxWidth={truncate}>{displayText}</TruncatedCell>
      </PanelLink>
    );
  }

  return (
    <Link
      to={href}
      target={target}
      className="tw-text-sm tw-text-primary hover:tw-underline"
    >
      <TruncatedCell maxWidth={truncate}>{displayText}</TruncatedCell>
    </Link>
  );
});

interface NumericCellProps {
  value: number | undefined | null;
  format?: (value: number) => string;
  className?: string;
  align?: "left" | "center" | "right";
}

export const NumericCell = React.memo(function NumericCell({
  value,
  format,
  className,
  align = "left",
}: NumericCellProps) {
  const alignClass = {
    left: "tw-text-left",
    center: "tw-text-center",
    right: "tw-text-right",
  }[align];

  if (value === undefined || value === null) {
    return <DisplayValue value={null} className={cn(alignClass, className)} />;
  }

  const displayValue = format ? format(value) : String(value);
  return (
    <span className={cn("tw-text-sm tw-tabular-nums", alignClass, className)}>
      {displayValue}
    </span>
  );
});

interface EmailCellProps {
  email: string | null | undefined;
  truncate?: number;
  className?: string;
}

export const EmailCell = React.memo(function EmailCell({
  email,
  truncate,
  className,
}: EmailCellProps) {
  if (!email) return <DisplayValue value={null} />;

  const content = truncate ? (
    <TruncatedCell maxWidth={truncate}>
      <span className={cn("tw-text-sm tw-text-primary", className)}>
        {email}
      </span>
    </TruncatedCell>
  ) : (
    <span className={cn("tw-text-sm tw-text-primary", className)}>{email}</span>
  );

  const link = (
    <a
      href={`mailto:${email}`}
      className="tw-text-sm tw-text-primary hover:tw-underline"
    >
      {content}
    </a>
  );

  if (truncate) {
    return (
      <Tooltip>
        <TooltipTrigger asChild={true}>{link}</TooltipTrigger>
        <TooltipContent>{email}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
});

interface PhoneCellProps {
  phone: string | number | null | undefined;
  truncate?: number;
  className?: string;
}

const sanitizePhone = (value: string) => value.replace(/[^\d+]/g, "");

const formatPhoneDisplay = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 12) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }
  return phone;
};

export const PhoneCell = React.memo(function PhoneCell({
  phone,
  truncate,
  className,
}: PhoneCellProps) {
  if (phone == null || phone === "") return <DisplayValue value={null} />;

  const rawText = String(phone);
  const text = formatPhoneDisplay(rawText);
  const hrefValue = sanitizePhone(rawText);

  const content = truncate ? (
    <TruncatedCell maxWidth={truncate}>
      <span
        className={cn(
          "tw-text-sm tw-text-primary tw-whitespace-nowrap",
          className
        )}
      >
        {text}
      </span>
    </TruncatedCell>
  ) : (
    <span
      className={cn(
        "tw-text-sm tw-text-primary tw-whitespace-nowrap",
        className
      )}
    >
      {text}
    </span>
  );

  if (!hrefValue) return content;

  const link = (
    <a
      href={`tel:${hrefValue}`}
      className="tw-text-sm tw-text-primary hover:tw-underline"
    >
      {content}
    </a>
  );

  if (truncate) {
    return (
      <Tooltip>
        <TooltipTrigger asChild={true}>{link}</TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
});

interface BooleanCellProps {
  value: boolean | null | undefined;
}

export const BooleanCell = React.memo(function BooleanCell({
  value,
}: BooleanCellProps) {
  if (value == null) return <DisplayValue value={null} />;

  return (
    <span
      className={cn(
        "tw-text-sm tw-font-medium",
        value ? "tw-text-green-600" : "tw-text-rose-600"
      )}
    >
      {value ? strings("common.yes") : strings("common.no")}
    </span>
  );
});

interface CurrencyCellProps {
  amount: number | null | undefined;
  currency?: string | null;
  locale?: string;
}

export const CurrencyCell = React.memo(function CurrencyCell({
  amount,
  currency,
  locale,
}: CurrencyCellProps) {
  if (amount == null || Number.isNaN(amount))
    return <DisplayValue value={null} />;

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  const currencyText = currency ? currency.toUpperCase() : "";

  return (
    <span className="tw-inline-flex tw-items-baseline tw-gap-1">
      <span className="tw-text-sm">{formatted}</span>
      {currencyText && (
        <span className="tw-text-xs tw-text-muted-foreground">
          {currencyText}
        </span>
      )}
    </span>
  );
});

interface CopyButtonProps {
  value: string | number;
  className?: string;
}

export const CopyButton = React.memo(function CopyButton({
  value,
  className,
}: CopyButtonProps) {
  const text = String(value);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!navigator?.clipboard) return;
      navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
    },
    [text]
  );

  return (
    <button
      type="button"
      className={cn(
        "tw-h-6 tw-w-6 tw-p-0 tw-flex tw-items-center tw-justify-center tw-rounded hover:tw-bg-muted tw-flex-shrink-0",
        className
      )}
      onClick={handleCopy}
      aria-label={strings("common.copy")}
      title={strings("common.copy")}
    >
      {copied ? (
        <Check className="tw-h-4 tw-w-4 tw-text-green-500" />
      ) : (
        <Copy className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
      )}
    </button>
  );
});

interface CodeCellProps {
  value: string | number | null | undefined;
  truncate?: number;
  className?: string;
}

const CodeCellWithHooks: React.FC<CodeCellProps> = ({
  value,
  truncate,
  className,
}) => {
  const text = String(value);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!navigator?.clipboard) return;
      navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
    },
    [text]
  );

  const textNode = (
    <span className={cn("tw-font-mono tw-text-sm", className)}>{text}</span>
  );

  const displayNode = truncate ? (
    <TruncatedCell maxWidth={truncate}>{textNode}</TruncatedCell>
  ) : (
    textNode
  );

  return (
    <div className="tw-inline-flex tw-items-center tw-gap-2">
      <button
        type="button"
        className="tw-h-6 tw-w-6 tw-p-0 tw-flex tw-items-center tw-justify-center tw-rounded hover:tw-bg-muted"
        onClick={handleCopy}
        aria-label={strings("common.copy")}
        title={strings("common.copy")}
      >
        {copied ? (
          <Check className="tw-h-4 tw-w-4 tw-text-green-500" />
        ) : (
          <Copy className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
        )}
      </button>
      {displayNode}
    </div>
  );
};

export const CodeCell = React.memo(function CodeCell({
  value,
  truncate,
  className,
}: CodeCellProps) {
  if (value == null || value === "") {
    return (
      <div className="tw-inline-flex tw-items-center">
        <span className="tw-w-6 tw-mr-2" /> {/* Spacer matching copy button */}
        <DisplayValue value={null} />
      </div>
    );
  }
  return (
    <CodeCellWithHooks
      value={value}
      truncate={truncate}
      className={className}
    />
  );
});
